/**
 * Proceduralne tekstury Kawiarni „Kość" — gdy brak PNG obj_cafe_*.
 * Zero napisów / emoji tekstowych; pastelowe kształty (wzorzec: park/playgroundVisuals).
 */
import Phaser from 'phaser';
import { CAFE_OBJECT_DEFS } from './objects.js';

const TEX_SIZE = 160;

export type CafeDecorKind =
  | 'cupcake'
  | 'espresso'
  | 'tray'
  | 'pitcher'
  | 'chair'
  | 'bell'
  | 'menu'
  | 'sugar'
  | 'plant';

/** Mapa texture key → rodzaj rysunku. */
export const CAFE_TEXTURE_SHAPES: Readonly<Record<string, CafeDecorKind>> = {
  obj_cafe_cupcake: 'cupcake',
  obj_cafe_espresso: 'espresso',
  obj_cafe_tray: 'tray',
  obj_cafe_pitcher: 'pitcher',
  obj_cafe_chair: 'chair',
  obj_cafe_bell: 'bell',
  obj_cafe_menu: 'menu',
  obj_cafe_sugar: 'sugar',
  obj_cafe_plant: 'plant',
};

/**
 * Upewnij się, że wszystkie tekstury kawiarni istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie.
 */
export function ensureCafeTextures(scene: Phaser.Scene): void {
  for (const def of CAFE_OBJECT_DEFS) {
    ensureCafeTexture(scene, def.texture);
  }
}

export function ensureCafeTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = CAFE_TEXTURE_SHAPES[textureKey];
  if (!kind) return textureKey;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawCafeShape(g, kind, TEX_SIZE, TEX_SIZE);
  g.generateTexture(textureKey, TEX_SIZE, TEX_SIZE);
  g.destroy();
  return textureKey;
}

/** Czysty opis kształtu (testowalny). */
export function cafeShapeForTexture(
  textureKey: string,
): CafeDecorKind | null {
  return CAFE_TEXTURE_SHAPES[textureKey] ?? null;
}

export function cafeTextureKeys(): string[] {
  return Object.keys(CAFE_TEXTURE_SHAPES);
}

export function drawCafeShape(
  g: Phaser.GameObjects.Graphics,
  kind: CafeDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;

  switch (kind) {
    case 'cupcake':
      drawCupcake(g, cx, cy);
      break;
    case 'espresso':
      drawEspresso(g, cx, cy);
      break;
    case 'tray':
      drawTray(g, cx, cy);
      break;
    case 'pitcher':
      drawPitcher(g, cx, cy);
      break;
    case 'chair':
      drawChair(g, cx, cy);
      break;
    case 'bell':
      drawBell(g, cx, cy);
      break;
    case 'menu':
      drawMenu(g, cx, cy);
      break;
    case 'sugar':
      drawSugar(g, cx, cy);
      break;
    case 'plant':
      drawPlant(g, cx, cy);
      break;
  }
}

function drawCupcake(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // papilotka
  g.fillStyle(0xffb74d, 1);
  g.fillRoundedRect(cx - 28, cy + 4, 56, 36, 6);
  g.fillStyle(0xffe0b2, 1);
  for (let i = 0; i < 5; i++) {
    g.fillRect(cx - 24 + i * 11, cy + 8, 6, 28);
  }
  // krem
  g.fillStyle(0xffc1e3, 1);
  g.fillEllipse(cx, cy - 6, 64, 40);
  g.fillStyle(0xfff0f7, 0.9);
  g.fillEllipse(cx - 8, cy - 14, 22, 14);
  // wisienka
  g.fillStyle(0xef5350, 1);
  g.fillCircle(cx + 4, cy - 28, 10);
  g.lineStyle(3, 0x66bb6a, 1);
  g.lineBetween(cx + 4, cy - 36, cx + 14, cy - 48);
  // cień
  g.fillStyle(0xc68642, 0.35);
  g.fillEllipse(cx, cy + 48, 50, 12);
}

function drawEspresso(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // korpus maszyny
  g.fillStyle(0xb0bec5, 1);
  g.fillRoundedRect(cx - 40, cy - 36, 80, 72, 10);
  g.lineStyle(4, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 40, cy - 36, 80, 72, 10);
  // górny panel
  g.fillStyle(0x78909c, 1);
  g.fillRoundedRect(cx - 36, cy - 48, 72, 18, 6);
  // filiżanka
  g.fillStyle(0xfff8f0, 1);
  g.fillRoundedRect(cx - 14, cy + 8, 28, 22, 4);
  g.fillStyle(0x4e342e, 1);
  g.fillEllipse(cx, cy + 12, 22, 10);
  // uchwyt
  g.lineStyle(4, 0xfff8f0, 1);
  g.strokeCircle(cx + 20, cy + 16, 8);
  // knobs
  g.fillStyle(0xef9a9a, 1);
  g.fillCircle(cx - 18, cy - 20, 6);
  g.fillStyle(0x90caf9, 1);
  g.fillCircle(cx + 18, cy - 20, 6);
  // para
  g.fillStyle(0xffffff, 0.5);
  g.fillCircle(cx - 6, cy - 58, 8);
  g.fillCircle(cx + 8, cy - 64, 6);
}

function drawTray(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xe0e0e0, 1);
  g.fillEllipse(cx, cy + 4, 110, 48);
  g.fillStyle(0xffffff, 1);
  g.fillEllipse(cx, cy, 100, 40);
  g.lineStyle(4, 0xbdbdbd, 1);
  g.strokeEllipse(cx, cy, 100, 40);
  // serwetka
  g.fillStyle(0xffcdd2, 0.85);
  g.fillRoundedRect(cx - 28, cy - 10, 56, 20, 4);
}

function drawPitcher(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // dzbanek
  g.fillStyle(0x90caf9, 1);
  g.fillRoundedRect(cx - 24, cy - 30, 48, 64, 12);
  g.fillStyle(0xbbdefb, 0.8);
  g.fillRoundedRect(cx - 16, cy - 20, 18, 40, 6);
  // dziobek
  g.fillStyle(0x64b5f6, 1);
  g.fillTriangle(cx + 20, cy - 24, cx + 42, cy - 8, cx + 20, cy);
  // uchwyt
  g.lineStyle(6, 0x42a5f5, 1);
  g.strokeCircle(cx - 30, cy + 4, 14);
  // mleko
  g.fillStyle(0xffffff, 0.9);
  g.fillEllipse(cx, cy - 28, 36, 12);
}

function drawChair(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // siedzisko
  g.fillStyle(0xffcc80, 1);
  g.fillRoundedRect(cx - 36, cy, 72, 22, 8);
  g.lineStyle(3, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 36, cy, 72, 22, 8);
  // oparcie
  g.fillStyle(0xffb74d, 1);
  g.fillRoundedRect(cx - 32, cy - 48, 64, 50, 10);
  g.fillStyle(0xffe0b2, 0.7);
  g.fillRoundedRect(cx - 22, cy - 38, 44, 30, 6);
  // nogi
  g.fillStyle(0xa1887f, 1);
  g.fillRect(cx - 30, cy + 20, 8, 28);
  g.fillRect(cx + 22, cy + 20, 8, 28);
  g.fillStyle(0x8d6e63, 0.5);
  g.fillEllipse(cx, cy + 52, 70, 12);
}

function drawBell(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // dzwonek
  g.fillStyle(0xffd54f, 1);
  g.fillEllipse(cx, cy + 8, 64, 48);
  g.fillTriangle(cx - 32, cy + 4, cx + 32, cy + 4, cx, cy - 36);
  g.fillStyle(0xffecb3, 0.9);
  g.fillEllipse(cx - 8, cy - 8, 18, 14);
  // guzik
  g.fillStyle(0xff8a65, 1);
  g.fillCircle(cx, cy - 40, 8);
  // podstawa
  g.fillStyle(0xffb300, 1);
  g.fillRoundedRect(cx - 28, cy + 28, 56, 12, 4);
  // dźwięk (łuki)
  g.lineStyle(3, 0xffe082, 0.8);
  g.strokeCircle(cx + 36, cy - 20, 12);
  g.strokeCircle(cx + 44, cy - 28, 16);
}

function drawMenu(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // tabliczka menu (bez napisów — kolorowe paski)
  g.fillStyle(0x8d6e63, 1);
  g.fillRoundedRect(cx - 32, cy - 44, 64, 88, 6);
  g.fillStyle(0xfff8e1, 1);
  g.fillRoundedRect(cx - 26, cy - 38, 52, 76, 4);
  g.fillStyle(0xffab91, 1);
  g.fillRoundedRect(cx - 18, cy - 28, 36, 8, 2);
  g.fillStyle(0x80cbc4, 1);
  g.fillRoundedRect(cx - 18, cy - 10, 36, 8, 2);
  g.fillStyle(0xce93d8, 1);
  g.fillRoundedRect(cx - 18, cy + 8, 36, 8, 2);
  g.fillStyle(0xffe082, 1);
  g.fillRoundedRect(cx - 18, cy + 26, 28, 8, 2);
  // kość-ozdoba
  g.fillStyle(0xffcc80, 1);
  g.fillEllipse(cx, cy - 52, 20, 10);
  g.fillCircle(cx - 10, cy - 56, 6);
  g.fillCircle(cx + 10, cy - 56, 6);
  g.fillCircle(cx - 10, cy - 48, 6);
  g.fillCircle(cx + 10, cy - 48, 6);
}

function drawSugar(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // cukierniczka
  g.fillStyle(0xffffff, 1);
  g.fillRoundedRect(cx - 22, cy - 16, 44, 48, 8);
  g.lineStyle(3, 0xe0e0e0, 1);
  g.strokeRoundedRect(cx - 22, cy - 16, 44, 48, 8);
  // pokrywka
  g.fillStyle(0xffecb3, 1);
  g.fillEllipse(cx, cy - 20, 48, 18);
  g.fillStyle(0xffe082, 1);
  g.fillCircle(cx, cy - 28, 8);
  // kryształki
  g.fillStyle(0xf5f5f5, 1);
  g.fillCircle(cx - 8, cy + 4, 4);
  g.fillCircle(cx + 6, cy + 12, 3);
  g.fillCircle(cx - 2, cy + 18, 3);
}

function drawPlant(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  // doniczka
  g.fillStyle(0xd7a86e, 1);
  g.fillTriangle(cx - 30, cy + 8, cx + 30, cy + 8, cx + 18, cy + 48);
  g.fillTriangle(cx - 30, cy + 8, cx + 18, cy + 48, cx - 18, cy + 48);
  g.fillStyle(0xc68642, 1);
  g.fillRoundedRect(cx - 32, cy, 64, 14, 4);
  // liście
  g.fillStyle(0x66bb6a, 1);
  g.fillEllipse(cx, cy - 20, 50, 36);
  g.fillStyle(0x81c784, 1);
  g.fillEllipse(cx - 18, cy - 36, 28, 24);
  g.fillEllipse(cx + 16, cy - 32, 26, 22);
  g.fillStyle(0xa5d6a7, 0.9);
  g.fillEllipse(cx + 4, cy - 48, 20, 16);
  // kwiatki
  g.fillStyle(0xf48fb1, 1);
  g.fillCircle(cx - 10, cy - 40, 6);
  g.fillStyle(0xfff59d, 1);
  g.fillCircle(cx + 12, cy - 44, 5);
}
